// Generic helpers

import { FADE_IN_ANIMATION_TIME, FADE_OUT_ANIMATION_TIME } from '../constants';

export const hasClass = (el: HTMLElement | Element, className: string): boolean => {
    if (el.classList) {
        return el.classList.contains(className);
    } else if (el.className) {
        return !!el.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'));
    } else {
        return false;
    }
};

export const addClass = (el: HTMLElement | Element, className: string): void => {
    if (el.classList) {
        el.classList.add(className);
    } else if (!hasClass(el, className)) {
        el.className += ' ' + className;
    }
};

export const removeClass = (el: HTMLElement | Element, className: string): void => {
    if (el.classList) {
        el.classList.remove(className);
    } else if (hasClass(el, className)) {
        const reg = new RegExp('(\\s|^)' + className + '(\\s|$)');
        el.className = el.className.replace(reg, ' ');
    }
};

// find the nearest parent element by selected method
export const getClosesParent = (el: Node, fn: (parentEl: Node) => boolean): Node | null => {
    return el && (fn(el) ? el : el.parentNode ? getClosesParent(el.parentNode, fn) : null);
};

// returns direct children elements by selector (e.g. classname)
export const getChildren = (element: Element, selectors: string): NodeListOf<Element> => {
    return element.querySelectorAll(selectors);
};

// ostripuje HTML tagy ze stringy pomoci vlozeni do virtualniho elementu v DOMu
// TODO: drive se to jmenovalo jen 'strip'
export const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

export const getURLParameter = (sParam: string): string | undefined => {
    const sPageURL = window.location.search.substring(1);
    const sURLVariables = sPageURL.split('&');
    if (sURLVariables.length) {
        for (let i = 0; i < sURLVariables.length; i++) {
            const sParameterName = sURLVariables[i].split('=');
            if (sParameterName[0] == sParam) {
                return sParameterName[1];
            }
        }
        return undefined;
    } else {
        return undefined;
    }
};

// vraci vysku okna vcetne pripadnych scrollbaru
export const getWindowsHeight = (): number => {
    return Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight,
    );
};

export const getRandomString = (length: number): string => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

export const isExternalLink = (obj: HTMLLinkElement | HTMLAnchorElement): boolean => {
    const href = obj.getAttribute('href');
    if (!href) {
        return false;
    }
    const isExcluded = href.match(/^tel:/) || href.match(/^mailto:/) || href.match(/^javascript:/) || href.match(/^#/);
    if (!isExcluded) {
        try {
            const url = new URL(href);
            return url.hostname != location.hostname;
        } catch (e) {
            return false;
        }
    } else {
        return false;
    }
};

export const isEmailLink = (obj: HTMLLinkElement | HTMLAnchorElement): boolean => {
    const href = obj.getAttribute('href');
    if (!href) {
        return false;
    }
    return !!href.match(/^mailto:/);
};

export const isDataFileLink = (obj: HTMLLinkElement | HTMLAnchorElement): boolean => {
    const href = obj.getAttribute('href');
    if (!href) {
        return false;
    }
    return !!(
        (
            href.match(/.*(data).*\.([a-zA-Z0-9]){3,4}$/) && // all files...
            !href.match(/.*(data).*\.(jpg|jpeg|gif|png|JPG|JPEG|GIF|PNG)$/)
        ) // ...but exclude images
    );
};

// funkce pro spozdene vykonani asynchronniho kodu (https://alvarotrigo.com/blog/wait-1-second-javascript/)
export const delay = (milliseconds: number): Promise<void> => {
    return new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });
};

export const fadeIn = (element: HTMLElement): void => {
    const defaultDisplayMode = element.getAttribute('data-display-mode') || 'block';
    element.style.display = defaultDisplayMode;
    setTimeout(function () {
        element.style.opacity = '1';
    }, FADE_IN_ANIMATION_TIME);
};

export const fadeOut = (element: HTMLElement): void => {
    element.style.opacity = '0';
    setTimeout(function () {
        element.style.display = 'none';
    }, FADE_OUT_ANIMATION_TIME);
};

export const fadeToggle = (element: HTMLElement): void => {
    const displayMode = element.style && element.style.display ? element.style.display : 'block';
    if (displayMode == 'none') {
        fadeIn(element);
    } else {
        fadeOut(element);
    }
};
